import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Post, NewsArticle } from '../../types';
import { newsService } from '../../services/newsService';
import NewsList from '../../components/features/NewsList';

const CommunityPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'article' | 'job' | 'column' | 'news'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [newsArticles, setNewsArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(false);

  // 模拟数据
  const mockPosts: Post[] = [
    {
      id: '1',
      title: '如何准备技术面试',
      content: '技术面试是进入科技公司的关键一步。本文将分享一些准备技术面试的技巧和经验...',
      author: {
        id: '1',
        uuid: 'user-1',
        name: '张明',
        email: 'zhangming@example.com',
        avatar: 'https://i.pravatar.cc/40?img=1',
        bio: '资深软件工程师',
        createdAt: '2023-01-01',
        updatedAt: '2023-01-01'
      },
      category: 'article',
      tags: ['面试', '技术'],
      createdAt: '2023-01-01',
      updatedAt: '2023-01-01'
    },
    // 添加更多模拟文章...
  ];

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const response = await newsService.getArticles(10, 0);
        setNewsArticles(response.items);
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };

    if (activeTab === 'news') {
      fetchNews();
    }
  }, [activeTab]);

  const filteredPosts = mockPosts.filter(post => {
    if (activeTab !== 'all' && post.category !== activeTab) return false;
    if (searchQuery) {
      return post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
             post.content.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 搜索栏 */}
      <div className="mb-8">
        <div className="relative rounded-md shadow-sm">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full rounded-md border-gray-300 pl-4 pr-12 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            placeholder="搜索文章..."
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* 分类标签 */}
      <div className="mb-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'all', name: '全部' },
              { id: 'article', name: '文章' },
              { id: 'job', name: '职位' },
              { id: 'column', name: '专栏' },
              { id: 'news', name: '新闻' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'all' | 'article' | 'job' | 'column' | 'news')}
                className={`${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* 文章和新闻列表 */}
      {activeTab === 'news' ? (
        <div className="w-full">
          {loading ? (
            <div className="text-center py-10">Loading news...</div>
          ) : (
            <>
              <NewsList limit={6} />
              <div className="text-center mt-6">
                <Link to="/news" className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">查看全部新闻</Link>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredPosts.map((post) => (
          <div key={post.id} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                  {post.category === 'article' ? '文章' : post.category === 'job' ? '职位' : '专栏'}
                </span>
                <span className="text-sm text-gray-500">
                  {new Date(post.createdAt).toLocaleDateString()}
                </span>
              </div>
              <h3 className="mt-2 text-lg font-medium text-gray-900">
                <Link to={`/community/posts/${post.id}`}>{post.title}</Link>
              </h3>
              <p className="mt-2 text-sm text-gray-500 line-clamp-3">{post.content}</p>
              <div className="mt-4 flex items-center">
                <div className="flex-shrink-0">
                  <img
                    className="h-10 w-10 rounded-full"
                    src={post.author.avatar || 'https://via.placeholder.com/40'}
                    alt={post.author.name}
                  />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    <Link to={`/profile/${post.author.id}`}>{post.author.name}</Link>
                  </p>
                  <p className="text-sm text-gray-500">{post.author.bio}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
        </div>
      )}

      {/* 发布按钮 */}
      <div className="fixed  right-8">
        <Link
          to="/community/posts/new"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <svg
            className="-ml-1 mr-2 h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          发布新内容
        </Link>
      </div>
    </div>
  );
};

export default CommunityPage;      